import ApiService from '@/common/api.service'
import driverAttachmentsModule from './driverAttachmentsModel'
import driverNotesModule from './driverNotesModule'

import * as utils from '@/common/utils'
import * as attachmentsHelpers from '@/store/attachments/attachments.helpers'
import * as notesHelpers from '@/store/notes/notes.helpers'
import * as security from '@/common/security'
import store from '@/store/index'

const module = 'assets/driversModule'
const attachmentsModule = module + '/attachments'
const notesModule = module + '/notes'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    name: '',
    contact_no: '',
    alt_contact_no: '',
    national_id: '',
    tag_no: '',
    drv_license_exp_date: '',
    drv_license_prev_date: '',
    pdp_license_exp_date: '',
    pdp_license_prev_date: '',
    spc_license_exp_date: '',
    spc_license_prev_date: '',
    partner: {
      id: null,
      name: ''
    },
    agency: {
      id: null,
      name: ''
    },
    client: {
      id: null,
      name: ''
    },
    created_at: '',
    updated_at: '',
    note: '',
    photos: []
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {
    partners: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param driver      Object
 * @param attachments Array|null
 * @param notes       Array|null
 *
 * @return Object
 */
function transformCreate (driver, attachments = null, notes = null) {
  return {
    id: driver.id,
    name: driver.name,
    contact_no: driver.contact_no,
    alt_contact_no: driver.alt_contact_no,
    national_id: driver.national_id,
    tag_no: driver.tag_no,
    partner_id: driver.partner.id,
    agency_id: driver.agency.id,
    client_id: driver.client.id,
    drv_license_exp_date: driver.drv_license_exp_date,
    drv_license_prev_date: driver.drv_license_prev_date,
    pdp_license_exp_date: driver.pdp_license_exp_date,
    pdp_license_prev_date: driver.pdp_license_prev_date,
    spc_license_exp_date: driver.spc_license_exp_date,
    spc_license_prev_date: driver.spc_license_prev_date,
    photos: driver.photos,
    attachments,
    notes
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param driver  Object
 *
 * @return Object
 */
function transformUpdate (driver) {
  return {
    id: driver.id,
    name: driver.name,
    contact_no: driver.contact_no,
    alt_contact_no: driver.alt_contact_no,
    national_id: driver.national_id,
    tag_no: driver.tag_no,
    partner_id: driver.partner.id,
    agency_id: driver.agency.id,
    client_id: driver.client.id,
    drv_license_exp_date: driver.drv_license_exp_date,
    drv_license_prev_date: driver.drv_license_prev_date,
    pdp_license_exp_date: driver.pdp_license_exp_date,
    pdp_license_prev_date: driver.pdp_license_prev_date,
    spc_license_exp_date: driver.spc_license_exp_date,
    spc_license_prev_date: driver.spc_license_prev_date,
    photos: driver.photos
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`drivers/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`drivers/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        attachmentsHelpers.purge(attachmentsModule)
        notesHelpers.purge(notesModule)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`drivers/${id}/edit/`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let notes = []
    let note = context.getters['get'].note || null
    if (note !== null && note.trim() !== '') {
      notes.push(note)
    }

    let attachments = attachmentsHelpers.attachments(attachmentsModule)
    const driver = context.getters['get']

    return ApiService
      .post(`drivers`, transformCreate(driver, attachments, notes))
      .then((response) => {
        attachmentsHelpers.purge(attachmentsModule)
        notesHelpers.purge(notesModule)

        context.commit('set', defaults())

        // Restore the partner, agency and client again because
        // we don't want to reset it after a post.
        context.commit('setPartnerIdName', driver.partner)
        context.commit('setAgencyIdName', driver.agency)
        context.commit('setClientIdName', driver.client)

        context.commit('setIsModified', false)
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const driver = context.getters['get']
    return ApiService
      .put(`drivers/${driver.id}`, transformUpdate(driver))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    const id = context.getters['get'].id
    return ApiService
      .delete(`drivers/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`drivers`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign({}, data)
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      name: data.name,
      contact_no: data.contact_no,
      alt_contact_no: data.alt_contact_no,
      national_id: data.national_id,
      tag_no: data.tag_no,
      drv_license_exp_date: data.drv_license_exp_date,
      drv_license_prev_date: data.drv_license_prev_date,
      pdp_license_exp_date: data.pdp_license_exp_date,
      pdp_license_prev_date: data.pdp_license_prev_date,
      spc_license_exp_date: data.spc_license_exp_date,
      spc_license_prev_date: data.spc_license_prev_date
    }
  },

  /**
   * Set the driver's partner, agency and client id's.
   *
   * @param state
   * @param client
   */
  setClient (state, client) {
    store.commit('assets/driversModule/clearClient')
    if (security.isAbovePartner()) {
      state.data.partner.id = client.partner.id
      state.data.partner.name = client.partner.name
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = client.agency.id
      state.data.agency.name = client.agency.name
    }
    if (security.isAboveClient() || security.belongsToMultipleClients()) {
      state.data.client.id = client.id
      state.data.client.name = client.name
    }
  },

  /**
   * Sets the partner id and name
   * @param state
   * @param partner
   */
  setPartnerIdName (state, partner) {
    state.data.partner.id = partner.id
    state.data.partner.name = partner.name
  },

  /**
   * Sets the agency id and name
   * @param state
   * @param agency
   */
  setAgencyIdName (state, agency) {
    state.data.agency.id = agency.id
    state.data.agency.name = agency.name
  },

  /**
   * Sets the client id and name
   * @param state
   * @param client
   */
  setClientIdName (state, client) {
    state.data.client.id = client.id
    state.data.client.name = client.name
  },

  /**
   * Clear the driver's partner agency and client data.
   *
   * @param state
   */
  clearClient (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    attachments: driverAttachmentsModule,
    notes: driverNotesModule
  }
}



// WEBPACK FOOTER //
// ./src/store/assets/drivers/driversModule.js